import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { AxiosInstance } from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { confirmRelink, getRelinkReview, Issue, RelinkAccount, RelinkChargebeeCustomer, RelinkReview } from '../../api/issues';
import { describeError } from '../../api/requeue';
import { Modal } from '../Modal/Modal';
import { RELINK_OUTCOME, rankedCandidates, relinkFields, suggestedAccount } from './exactCompare';
import './RelinkDialog.scss';

export interface IRelinkDialogProps {
    axios: AxiosInstance;
    issue: Issue | null;
    onClose: () => void;
    /** Called after a relink went through, to reload the lists. */
    onDone: () => void;
}

/**
 * A relink, decided by a person. On top who the customer is and where it pointed; on the left
 * the accounts it could point at, the best match first, with what matches; on the right the
 * chosen one field by field next to the Chargebee customer. Nothing happens until someone chose
 * an account and confirmed it is the same customer; the server checks the choice against Exact
 * again before it relinks.
 */
export const RelinkDialog = ({ axios, issue, onClose, onDone }: IRelinkDialogProps) => {
    const [review, setReview] = useState<RelinkReview | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [chosen, setChosen] = useState<string | null>(null);
    const [verified, setVerified] = useState(false);
    const [busy, setBusy] = useState(false);
    const [searching, setSearching] = useState(false);
    const [query, setQuery] = useState('');
    const [result, setResult] = useState<string | null>(null);

    const load = useCallback(
        (search?: string) => {
            if (!issue) {
                return;
            }

            setSearching(true);
            setError(null);

            getRelinkReview(axios, issue.customer, issue.environment, issue.key, search)
                .then((loaded) => {
                    setReview(loaded);
                    setVerified(false);

                    // Only a suggestion: the radio is set, the confirmation is not.
                    setChosen(suggestedAccount(loaded));
                })
                .catch((ex) => setError(describeError(ex)))
                .finally(() => setSearching(false));
        },
        [axios, issue]
    );

    useEffect(() => {
        setReview(null);
        setResult(null);
        setQuery('');
        load();
    }, [load]);

    const candidates = useMemo(() => (review ? rankedCandidates(review) : []), [review]);

    const picked = candidates.find((c) => c.id === chosen) ?? null;
    const canChoose = review?.current?.exists !== true && !result;
    const customer = review?.chargebee;

    const search = (event: FormEvent) => {
        event.preventDefault();
        load(query.trim() || undefined);
    };

    const confirm = () => {
        if (!issue || !chosen) {
            return;
        }

        setBusy(true);
        setError(null);

        confirmRelink(axios, issue.customer, issue.environment, issue.key, chosen, review?.query)
            .then((outcome) => {
                setResult(`${RELINK_OUTCOME[outcome.state] ?? outcome.state}${outcome.note ? ` ${outcome.note}` : ''}`);
                onDone();
            })
            .catch((ex) => setError(describeError(ex)))
            .finally(() => setBusy(false));
    };

    const content = (
        <div className="relink">
            {!review && !error && (
                <p className="relink__loading">
                    <FontAwesomeIcon icon="spinner" spin /> Klant en relaties ophalen…
                </p>
            )}
            {error && <p className="relink__alert relink__alert--error">{error}</p>}

            {review && (
                <>
                    <div className="relink__context">
                        <div className="relink__party">
                            <span className="relink__label">Chargebee-klant</span>
                            <strong>{customer?.exists ? customer.company || customer.name || customer.id : 'Onbekend in Chargebee'}</strong>
                            <span className="relink__sub">{customer?.id}</span>
                        </div>
                        <FontAwesomeIcon icon="arrow-right" className="relink__arrow" />
                        <div className={`relink__party${review.current?.exists ? '' : ' relink__party--gone'}`}>
                            <span className="relink__label">Nu gekoppeld in Exact</span>
                            <strong>{review.current?.code ? `Relatie ${review.current.code.trim()}` : 'Geen relatie'}</strong>
                            <span className="relink__sub">{review.current?.exists ? review.current.name : 'Bestaat niet meer'}</span>
                        </div>
                    </div>

                    {canChoose && (
                        <form className="relink__search" onSubmit={search}>
                            <FontAwesomeIcon icon="magnifying-glass" className="relink__search-icon" />
                            <input
                                className="form-control form-control-sm"
                                type="search"
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder="Zoek in Exact op naam, code, e-mail of btw-nummer"
                                aria-label="Zoek een relatie in Exact"
                            />
                            <button type="submit" className="btn btn-sm btn-outline-primary" disabled={searching}>
                                {searching ? <FontAwesomeIcon icon="spinner" spin /> : 'Zoeken'}
                            </button>
                        </form>
                    )}

                    {candidates.length > 0 ? (
                        <div className="relink__body">
                            <div className="relink__list" role="radiogroup" aria-label="Relaties in Exact">
                                <span className="relink__label">Kies de relatie ({candidates.length})</span>
                                {candidates.map((account) => (
                                    <Option
                                        key={account.id ?? account.code}
                                        account={account}
                                        customer={customer}
                                        chosen={account.id === chosen}
                                        proposed={account.id === review.proposedId}
                                        disabled={!canChoose}
                                        onChoose={() => {
                                            setChosen(account.id ?? null);
                                            setVerified(false);
                                        }}
                                    />
                                ))}
                            </div>

                            <div className="relink__compare">
                                {picked ? (
                                    <Compare customer={customer} account={picked} />
                                ) : (
                                    <p className="relink__empty">Kies links een relatie om hem naast de Chargebee-klant te zien.</p>
                                )}
                            </div>
                        </div>
                    ) : (
                        review.problem && <p className="relink__alert">{review.problem}</p>
                    )}

                    {candidates.length > 0 && review.problem && <p className="relink__alert">{review.problem}</p>}

                    {result ? (
                        <p className="relink__alert relink__alert--ok">
                            <FontAwesomeIcon icon="circle-check" /> {result}
                        </p>
                    ) : (
                        picked && (
                            <label className="relink__verify">
                                <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} />
                                <span>
                                    Ik heb gecontroleerd dat <strong>{picked.name ?? `relatie ${picked.code?.trim()}`}</strong> dezelfde klant is.
                                </span>
                            </label>
                        )
                    )}
                </>
            )}
        </div>
    );

    const footer = (
        <div className="d-flex gap-2 ms-auto">
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onClose}>
                {result ? 'Sluiten' : 'Annuleren'}
            </button>
            {!result && (
                <button type="button" className="btn btn-sm btn-primary" disabled={!chosen || !verified || busy} onClick={confirm}>
                    <FontAwesomeIcon icon={busy ? 'spinner' : 'link'} spin={busy} /> Omkoppelen en opnieuw aanbieden
                </button>
            )}
        </div>
    );

    return <Modal show={issue !== null} handleClose={onClose} title="Relatie kiezen" content={content} footer={footer} wide />;
};

/** One account to choose: name and code, and which of the telling fields match. */
const Option = ({
    account,
    customer,
    chosen,
    proposed,
    disabled,
    onChoose,
}: {
    account: RelinkAccount;
    customer?: RelinkChargebeeCustomer | null;
    chosen: boolean;
    proposed: boolean;
    disabled: boolean;
    onChoose: () => void;
}) => {
    const fields = relinkFields(customer, account).filter((f) => f.match !== null);
    const matches = fields.filter((f) => f.match);

    return (
        <label className={`relink__option${chosen ? ' is-chosen' : ''}${disabled ? ' is-disabled' : ''}`}>
            <input type="radio" name="relink-account" checked={chosen} onChange={onChoose} disabled={disabled} />
            <span className="relink__option-main">
                <span className="relink__option-title">
                    <strong>{account.name ?? 'Zonder naam'}</strong>
                    {account.code && <span className="relink__code">{account.code.trim()}</span>}
                    {account.blocked && <span className="relink__tag relink__tag--warn">geblokkeerd</span>}
                    {proposed && <span className="relink__tag relink__tag--info">voorstel scanner</span>}
                </span>
                <span className="relink__matches">
                    {matches.length > 0 ? (
                        matches.map((f) => (
                            <span key={f.label} className="relink__match">
                                <FontAwesomeIcon icon="check" /> {f.label}
                            </span>
                        ))
                    ) : (
                        <span className="relink__nomatch">Niets komt overeen</span>
                    )}
                </span>
                {account.found && <span className="relink__found">{account.found}</span>}
            </span>
            <span className={`relink__score relink__score--${matches.length >= 2 ? 'good' : matches.length === 1 ? 'some' : 'none'}`} title="Velden die overeenkomen">
                {matches.length}/{fields.length}
            </span>
        </label>
    );
};

/** The chosen account field by field next to the Chargebee customer. */
const Compare = ({ customer, account }: { customer?: RelinkChargebeeCustomer | null; account: RelinkAccount }) => (
    <table className="relink__table">
        <thead>
            <tr>
                <th aria-label="Veld" />
                <th>Chargebee</th>
                <th>Exact {account.code?.trim()}</th>
                <th aria-label="Overeenkomst" />
            </tr>
        </thead>
        <tbody>
            {relinkFields(customer, account).map((f) => (
                <tr key={f.label} className={f.match === true ? 'is-same' : f.match === false ? 'is-different' : undefined}>
                    <th>{f.label}</th>
                    <td>{f.chargebee || <span className="relink__none">–</span>}</td>
                    <td>{f.exact || <span className="relink__none">–</span>}</td>
                    <td className="relink__verdict">
                        {f.match === true && <FontAwesomeIcon icon="circle-check" title="Gelijk" />}
                        {f.match === false && <FontAwesomeIcon icon="circle-xmark" title="Wijkt af" />}
                    </td>
                </tr>
            ))}
        </tbody>
    </table>
);
